import { supabase } from "@/integrations/supabase/client";
import { contactFormRateLimit } from "@/utils/security";
import { initializeCSRFToken } from "@/utils/csrfProtection";
import { SecurityMonitor, RateLimitMonitor } from "@/utils/securityMonitoring";
import { validateContactForm } from "@/utils/enhancedValidation";

export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  projectType: string;
  workAmount: string;
  location: string;
  startDate: string;
  description: string;
  acceptContact: boolean;
  acceptData: boolean;
}

export interface ContactRecord {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  project_type: string;
  work_amount: string | null;
  location: string | null;
  start_date: string | null;
  description: string | null;
  accept_contact: boolean;
  accept_data: boolean;
  created_at: string;
  updated_at: string;
}

export class ContactService {
  /**
   * Submit contact form data securely to database
   */
  static async submitContact(formData: ContactFormData): Promise<{ success: boolean; error?: string }> {
    try {
      // Basic client-side rate limiting check (additional server-side check exists)
      const clientIdentifier = formData.email;
      const rateLimitCheck = contactFormRateLimit.isAllowed(clientIdentifier);
      
      if (!rateLimitCheck.allowed) {
        RateLimitMonitor.recordViolation(clientIdentifier, 'ContactForm');
        return {
          success: false,
          error: rateLimitCheck.message || "Trop de tentatives. Veuillez patienter avant de réessayer."
        };
      }

      // Enhanced client-side validation using comprehensive security validation
      const formValidation = validateContactForm({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        project_type: formData.projectType,
        location: formData.location,
        work_amount: formData.workAmount,
        description: formData.description
      });
      
      if (!formValidation.isValid) {
        const errorMessages = Object.values(formValidation.errors).flat();
        return {
          success: false,
          error: errorMessages[0] || "Données de formulaire invalides"
        };
      }

      // Add CSRF token to request
      const csrfToken = initializeCSRFToken();
      
      // Submit to secure edge function for server-side processing using sanitized data
      const { data, error } = await supabase.functions.invoke('submit-contact', {
        body: {
          firstName: formValidation.sanitizedData.first_name,
          lastName: formValidation.sanitizedData.last_name,
          email: formValidation.sanitizedData.email,
          phone: formValidation.sanitizedData.phone,
          projectType: formValidation.sanitizedData.project_type,
          workAmount: formValidation.sanitizedData.work_amount || '',
          location: formValidation.sanitizedData.location || '',
          startDate: '',
          description: formValidation.sanitizedData.description || '',
          acceptContact: formData.acceptContact,
          acceptData: formData.acceptData,
          csrf_token: csrfToken
        },
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Origin': window.location.origin
        }
      });

      if (error) {
        // Security: Log structured error data using security monitor
        SecurityMonitor.logFormEvent('submission_failed', 'contact', { 
          status: (error as any)?.context?.res?.status,
          errorType: (error as any)?.name || 'unknown'
        });
        return {
          success: false,
          error: "Une erreur technique s'est produite. Veuillez réessayer plus tard."
        };
      }

      // Handle response from edge function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || "Une erreur s'est produite lors de l'envoi."
        };
      }

      return { success: true };
    } catch (error) {
      SecurityMonitor.logFormEvent('unexpected_error', 'contact', { 
        errorMessage: error instanceof Error ? error.message.substring(0, 100) : 'unknown'
      });
      return {
        success: false,
        error: "Une erreur inattendue s'est produite. Veuillez réessayer."
      };
    }
  }

  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone format (French format)
   */
  static isValidPhone(phone: string): boolean {
    const phoneRegex = /^(?:(?:\+33|0)[1-9](?:[0-9]{8}))$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  }

  /**
   * Sanitize text input to prevent XSS and injection attacks
   */
  static sanitizeText(text: string): string {
    return text
      .trim()
      .replace(/[<>&"']/g, '') // Remove HTML tags and common XSS characters
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .replace(/script/gi, '') // Remove script tags
      .substring(0, 1000); // Limit length
  }


  /**
   * Validate and sanitize form data
   */
  static validateAndSanitizeForm(formData: ContactFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate and sanitize required fields
    if (!formData.firstName?.trim() || formData.firstName.length > 50) {
      errors.push("Le prénom est requis et doit faire moins de 50 caractères.");
    }
    
    if (!formData.lastName?.trim() || formData.lastName.length > 50) {
      errors.push("Le nom est requis et doit faire moins de 50 caractères.");
    }

    if (!this.isValidEmail(formData.email)) {
      errors.push("L'adresse email n'est pas valide.");
    }

    if (!this.isValidPhone(formData.phone)) {
      errors.push("Le numéro de téléphone n'est pas valide.");
    }

    if (!formData.projectType?.trim()) {
      errors.push("Le type de projet est requis.");
    }

    // Validate optional fields length
    if (formData.location && formData.location.length > 100) {
      errors.push("L'emplacement doit faire moins de 100 caractères.");
    }

    if (formData.description && formData.description.length > 2000) {
      errors.push("La description doit faire moins de 2000 caractères.");
    }

    // Check consent
    if (!formData.acceptContact || !formData.acceptData) {
      errors.push("Les consentements sont obligatoires.");
    }

    return { isValid: errors.length === 0, errors };
  }
}